import { useEffect, useRef, useState } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth, signInMicrosoft, signOut, EMULATOR_MODE } from './firebase';
import { useCollectionsAccess, useMe, useRequests, useSettings } from './lib/data';
import { useHashRoute } from './ui';
import { AllRequests } from './pages/AllRequests';
import { Home } from './pages/Home';
import { Kanban } from './pages/Kanban';
import { Lineup } from './pages/Lineup';
import { Planner } from './pages/Planner';
import { OrgBoard } from './pages/OrgBoard';
import { Completed } from './pages/Completed';
import { Collections } from './pages/Collections';
import { Admin } from './pages/Admin';
import { RequestDetail } from './pages/RequestDetail';
import { SubmitForm, MyRequests } from './pages/Submit';
import { QuickAdd } from './pages/QuickAdd';

/** Editor nav. Pages marked soon are approved in the spec and land in the next build. */
const NAV: { path: string; label: string; soon?: boolean }[] = [
  { path: '/', label: 'Home' },
  { path: '/kanban', label: 'Kanban' },
  { path: '/lineup', label: 'Lineup' },
  { path: '/org', label: 'Org board' },
  { path: '/planner', label: 'Planner' },
  { path: '/all', label: 'All' },
  { path: '/done', label: 'Completed' },
  { path: '/collections', label: 'Collections' },
  { path: '/admin', label: 'Admin' },
];

export function App() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [err, setErr] = useState<string | null>(null);
  const activeUid = useRef<string | null>(null);
  const [route, go] = useHashRoute();

  useEffect(() => onAuthStateChanged(auth, (u) => {
    if (u?.uid !== activeUid.current) { activeUid.current = u?.uid ?? null; setErr(null); }
    setUser(u);
  }), []);

  const { me, loading } = useMe(user ?? null);
  const { items, error } = useRequests(me);
  const settings = useSettings(me);
  const collectionsAccess = useCollectionsAccess(me);
  const [quickOpen, setQuickOpen] = useState(false);

  if (user === undefined || (user && loading)) return <div className="wrap"><p className="muted">Loading...</p></div>;

  if (user === null || !me) {
    return (
      <div className="wrap narrow">
        <header className="hub"><h1>Tech Request Hub</h1><span className="env">{EMULATOR_MODE ? 'emulator' : ''}</span></header>
        <div className="card">
          <h2>Sign in</h2>
          <p className="muted">Use your Trust Roofing Microsoft account to submit or track a tech request.</p>
          <button onClick={() => signInMicrosoft().catch((e) => setErr(e.message))}>Sign in with Microsoft</button>
          {err && <p className="err">{err}</p>}
        </div>
      </div>
    );
  }

  // Staff: the intake form + their own requests. Nothing else.
  if (me.role !== 'editor') {
    return (
      <div className="wrap narrow">
        <header className="hub">
          <h1>Tech Request Hub</h1>
          <span className="spacer" />
          <button className="secondary" onClick={() => signOut()}>Sign out</button>
        </header>
        <SubmitForm me={me} />
        <MyRequests items={items} />
      </div>
    );
  }

  const [path, qs] = route.split('?');
  const params = new URLSearchParams(qs || '');
  const detail = path.match(/^\/r\/([A-Za-z0-9_-]+)$/);
  const page = NAV.find((n) => n.path === path);

  return (
    <div className="wrap wide">
      <header className="hub">
        <h1 onClick={() => go('/')} className="home-link">Tech Request Hub</h1>
        <nav className="tabs">
          {NAV.filter((n) => n.path !== '/collections' || collectionsAccess === true).map((n) => (
            <button key={n.path} className={path === n.path ? 'on' : ''} onClick={() => go(n.path)} title={n.soon ? 'Next build' : ''}>
              {n.label}{n.soon && <span className="soon">soon</span>}
            </button>
          ))}
        </nav>
        <span className="spacer" />
        <button onClick={() => setQuickOpen(true)}>+ Quick add</button>
        <button className="secondary" onClick={() => go('/submit')}>Form</button>
        <span className="who" title={me.email}>{me.name}</span>
        <button className="secondary" onClick={() => signOut()}>Sign out</button>
      </header>

      {error && <p className="err">{error}</p>}
      {quickOpen && <QuickAdd me={me} onClose={() => setQuickOpen(false)} onCreated={(id) => { setQuickOpen(false); go(`/r/${id}`); }} />}

      {detail ? <RequestDetail me={me} id={detail[1]} items={items} settings={settings} onBack={() => history.back()} />
        : path === '/submit' ? <div className="narrow-inner"><SubmitForm me={me} /></div>
        : page?.soon ? <div className="card"><h2>{page.label}</h2><p className="muted">Approved in the spec - arriving in the next build.</p></div>
        : path === '/kanban' ? <Kanban key={qs} me={me} items={items} settings={settings} go={go} initial={{ division: params.get('division') ?? '' }} />
        : path === '/lineup' ? <Lineup me={me} items={items} settings={settings} go={go} />
        : path === '/planner' ? <Planner me={me} items={items} settings={settings} go={go} />
        : path === '/org' ? <OrgBoard items={items} settings={settings} go={go} />
        : path === '/done' ? <Completed items={items} settings={settings} go={go} />
        : path === '/collections' ? <Collections me={me} access={collectionsAccess} />
        : path === '/admin' ? <Admin me={me} settings={settings} />
        : path === '/all' ? <AllRequests items={items} settings={settings} onOpen={(id) => go(`/r/${id}`)} />
        : <Home me={me} items={items} settings={settings} go={go} />}
    </div>
  );
}
